import { LEVELS, LEVEL_ORDER } from "../levels";
import type { Level } from "../levels/types";

/** Só no browser. Não misturar com `onirya.defaults.*`. */
export const CAMPAIGN_STORAGE_KEY = "onirya.campaign";

export type Campaign = {
  cleared: string[];
};

export function loadCampaign(): Campaign {
  try {
    const raw = localStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return { cleared: [] };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !("cleared" in parsed)) {
      return { cleared: [] };
    }
    const list = (parsed as { cleared: unknown }).cleared;
    if (!Array.isArray(list)) return { cleared: [] };
    const allowed = new Set(LEVEL_ORDER);
    const cleared: string[] = [];
    for (const id of list) {
      if (typeof id !== "string" || !allowed.has(id) || cleared.includes(id)) continue;
      cleared.push(id);
    }
    return { cleared };
  } catch {
    return { cleared: [] };
  }
}

export function saveCampaign(campaign: Campaign): void {
  localStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify({ cleared: campaign.cleared }));
}

export function markLevelCleared(id: string): void {
  if (!LEVELS[id]) return;
  const campaign = loadCampaign();
  if (campaign.cleared.includes(id)) return;
  campaign.cleared.push(id);
  saveCampaign(campaign);
}

export function isLevelCleared(id: string): boolean {
  return loadCampaign().cleared.includes(id);
}

export function isLevelUnlocked(id: string): boolean {
  const i = LEVEL_ORDER.indexOf(id);
  if (i <= 0) return true;
  const prev = LEVEL_ORDER[i - 1];
  return !!prev && isLevelCleared(prev);
}

export function nextLevelId(afterId: string): string | null {
  const i = LEVEL_ORDER.indexOf(afterId);
  if (i < 0 || i >= LEVEL_ORDER.length - 1) return null;
  return LEVEL_ORDER[i + 1] ?? null;
}

/** Primeiro nível ainda não passado; se todos passaram, o último. */
export function continueLevelId(): string {
  for (const id of LEVEL_ORDER) {
    if (!isLevelCleared(id)) return id;
  }
  return LEVEL_ORDER[LEVEL_ORDER.length - 1] ?? "onirya";
}

export function hasCampaignProgress(): boolean {
  return loadCampaign().cleared.length > 0;
}

export function devLevelOverride(): Level | null {
  if (!import.meta.env.DEV) return null;
  const wanted = new URLSearchParams(location.search).get("level");
  if (!wanted) return null;
  return LEVELS[wanted] ?? null;
}

export function getBootLevel(): Level {
  const dev = devLevelOverride();
  if (dev) return dev;
  const wanted = new URLSearchParams(location.search).get("level");
  if (wanted && LEVELS[wanted] && isLevelUnlocked(wanted)) return LEVELS[wanted];
  const id = continueLevelId();
  const level = LEVELS[id] ?? LEVELS[LEVEL_ORDER[0]];
  if (!level) throw new Error("Nenhum nível para começar");
  return level;
}
